import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { ChevronRight, Loader2, Search, SearchX } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { AgentLogoImage } from "@/components/agents/AgentLogoImage";
import { useFileUrl } from "@/hooks/use-file-url";
import {
	coerceAgentCategories,
	getAgentCategoryLabel,
} from "@/lib/agent-categories";
import { type Currency, currencyAtom } from "@/lib/currency";
import { formatCurrency as formatCurrencyUtil } from "@/lib/format";
import {
	getHomepageAgentRouteSegment,
	HOMEPAGE_SEARCH_MIN_QUERY_LENGTH,
	isHomepageSearchEnabled,
	normalizeHomepageSearchQuery,
	shouldOpenHomepageSearchPanel,
} from "@/lib/homepage-search";
import type { PublicAgentView } from "@/lib/public-agents";
import { cn } from "@/lib/utils";

type HomepageAgentSearchResult = {
	categories?: string[];
	displayAvatarMode: PublicAgentView["displayAvatarMode"];
	displayDescription: string;
	displayFounderName: string;
	displayPathSegment?: string;
	displayWebsite?: string | null;
	estimatedMrr: number;
	id: string;
	logoUrl?: string | null;
	slug: string;
	name?: string;
	displayName?: string;
};

type HomepageFounderSearchResult = {
	avatarUrl: string | null;
	userImage?: string | null;
	name: string;
	slug: string;
	stats: {
		allTimeRevenue: number;
		estimatedMrr: number;
		totalAgents: number;
	};
};

function categoryAccentClass(category: string) {
	switch (category) {
		case "ai":
			return "bg-indigo-500";
		case "development":
			return "bg-emerald-600";
		case "finance":
			return "bg-violet-500";
		case "productivity":
			return "bg-sky-500";
		case "marketing":
			return "bg-rose-500";
		case "ecommerce":
			return "bg-teal-600";
		case "analytics":
			return "bg-slate-500";
		case "design":
			return "bg-pink-500";
		case "automation":
			return "bg-purple-500";
		case "education":
			return "bg-sky-600";
		case "healthcare":
			return "bg-red-500";
		case "communication":
			return "bg-cyan-500";
		case "content":
			return "bg-fuchsia-500";
		case "sales":
			return "bg-slate-600";
		case "customer-support":
			return "bg-cyan-600";
		case "recruiting":
		case "hr":
			return "bg-violet-600";
		case "real-estate":
			return "bg-stone-500";
		case "travel":
			return "bg-teal-500";
		case "security":
			return "bg-zinc-600";
		case "communities":
			return "bg-blue-500";
		case "seo":
			return "bg-amber-500";
		case "media":
			return "bg-purple-600";
		case "gaming":
			return "bg-green-500";
		case "infrastructure":
			return "bg-orange-600";
		case "legal":
			return "bg-stone-600";
		case "data":
			return "bg-yellow-600";
		case "directories":
			return "bg-neutral-500";
		case "agencies":
			return "bg-gray-600";
		default:
			return "bg-zinc-500";
	}
}

export function InitialAvatar({
	name,
	category,
	avatarUrl,
	logoUrl,
	mode,
	size,
	website,
}: {
	name: string;
	category: string;
	avatarUrl?: string | null;
	logoUrl?: string | null;
	mode?: "brand" | "stealth";
	size: "agent" | "founder";
	website?: string | null;
}) {
	const initials = name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();
	const { url: resolvedAvatarUrl } = useFileUrl(avatarUrl);
	const [failedAvatarUrl, setFailedAvatarUrl] = useState<string | null>(null);

	if (
		resolvedAvatarUrl &&
		failedAvatarUrl !== resolvedAvatarUrl &&
		size === "founder" &&
		mode !== "stealth"
	) {
		return (
			<img
				alt={name}
				className="size-7 shrink-0 border border-border bg-muted object-cover"
				onError={() => setFailedAvatarUrl(resolvedAvatarUrl)}
				referrerPolicy="no-referrer"
				src={resolvedAvatarUrl}
			/>
		);
	}

	if (size === "agent" && mode !== "stealth" && (logoUrl || website)) {
		return (
			<AgentLogoImage
				logoUrl={logoUrl}
				name={initials}
				size="sm"
				website={website}
			/>
		);
	}

	return (
		<div
			className={`inline-flex shrink-0 items-center justify-center font-mono ${
				mode === "stealth"
					? "bg-zinc-700 text-white"
					: size === "founder"
						? "border border-border bg-muted text-foreground"
						: `${categoryAccentClass(category)} text-white`
			} ${size === "agent" ? "size-8 text-sm" : "size-7 text-sm"}`}
		>
			{initials}
		</div>
	);
}

function FounderSearchAvatar({
	founder,
}: {
	founder: Pick<
		HomepageFounderSearchResult,
		"avatarUrl" | "userImage" | "name"
	>;
}) {
	const initials = founder.name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();
	const imageSrc = founder.avatarUrl ?? founder.userImage ?? null;
	const { url: resolvedImageSrc } = useFileUrl(imageSrc);
	const [errored, setErrored] = useState(false);

	if (resolvedImageSrc && !errored) {
		return (
			<img
				src={resolvedImageSrc}
				alt={founder.name}
				onError={() => setErrored(true)}
				className="size-8 shrink-0 border border-border object-cover bg-muted"
				referrerPolicy="no-referrer"
			/>
		);
	}

	return (
		<div
			className={cn(
				"inline-flex size-8 shrink-0 items-center justify-center border border-border bg-muted",
				"font-mono text-sm text-foreground",
			)}
		>
			{initials}
		</div>
	);
}

function formatFounderSummary(
	founder: HomepageFounderSearchResult,
	currency: Currency,
) {
	const totalEarnings = formatCurrencyUtil(
		founder.stats.allTimeRevenue,
		currency,
	);
	return `${new Intl.NumberFormat("en-US").format(founder.stats.totalAgents)} startup${founder.stats.totalAgents === 1 ? "" : "s"} · ${totalEarnings}`;
}

function SearchResultSection({
	title,
	count,
	children,
}: {
	children: ReactNode;
	title: string;
	count?: number;
}) {
	return (
		<section className="border-t border-border first:border-t-0">
			<div className="flex items-center justify-between px-3 py-2 border-b border-border">
				<h3 className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; {title}
				</h3>
				{typeof count === "number" && count > 0 ? (
					<span className="font-mono text-sm tabular-nums text-muted-foreground/60">
						{String(count)}
					</span>
				) : null}
			</div>
			<div>{children}</div>
		</section>
	);
}

function SearchPanelMessage({
	icon,
	children,
}: {
	icon?: ReactNode;
	children: ReactNode;
}) {
	return (
		<div className="flex flex-col items-center justify-center gap-2 px-5 py-8 text-center font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
			{icon}
			<div>{children}</div>
		</div>
	);
}

function SearchSectionEmpty({ children }: { children: ReactNode }) {
	return (
		<div className="p-3 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground/70">
			{children}
		</div>
	);
}

export function HomepageSearch({ joined }: { joined?: boolean } = {}) {
	const [query, setQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState("");
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const inputId = useId();
	const panelId = useId();
	const currency = useAtomValue(currencyAtom);
	const normalizedDebouncedQuery = normalizeHomepageSearchQuery(debouncedQuery);
	const isSearchEnabled = isHomepageSearchEnabled(normalizedDebouncedQuery);
	const isPanelOpen = shouldOpenHomepageSearchPanel(query, open);

	useEffect(() => {
		const timeoutId = window.setTimeout(() => {
			setDebouncedQuery(query);
		}, 220);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [query]);

	useEffect(() => {
		if (!isPanelOpen) return;

		const handlePointerDown = (event: PointerEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setOpen(false);
			}
		};

		document.addEventListener("pointerdown", handlePointerDown);
		return () => {
			document.removeEventListener("pointerdown", handlePointerDown);
		};
	}, [isPanelOpen]);

	const agentsQuery = useQuery({
		enabled: isSearchEnabled,
		queryFn: async () => {
			const response = await fetch(
				`/api/agents/search?query=${encodeURIComponent(normalizedDebouncedQuery)}&limit=5`,
				{ headers: { Accept: "application/json" } },
			);

			if (!response.ok) {
				throw new Error("Failed to search agents");
			}

			return response.json() as Promise<{
				agents: HomepageAgentSearchResult[];
			}>;
		},
		queryKey: ["homepage-search", "agents", normalizedDebouncedQuery],
	});

	const foundersQuery = useQuery({
		enabled: isSearchEnabled,
		queryFn: async () => {
			const response = await fetch(
				`/api/founders/search?query=${encodeURIComponent(normalizedDebouncedQuery)}&limit=5`,
				{ headers: { Accept: "application/json" } },
			);

			if (!response.ok) {
				throw new Error("Failed to search founders");
			}

			return response.json() as Promise<{
				founders: HomepageFounderSearchResult[];
			}>;
		},
		queryKey: ["homepage-search", "founders", normalizedDebouncedQuery],
	});

	const agents = isSearchEnabled ? (agentsQuery.data?.agents ?? []) : [];
	const founders = isSearchEnabled ? (foundersQuery.data?.founders ?? []) : [];
	const isLoading = isSearchEnabled
		? agentsQuery.isFetching || foundersQuery.isFetching
		: false;
	const hasResults = agents.length > 0 || founders.length > 0;
	const hasError =
		isSearchEnabled &&
		!isLoading &&
		(agentsQuery.isError || foundersQuery.isError);

	return (
		<>
			{isPanelOpen && (
				<button
					type="button"
					aria-hidden="true"
					tabIndex={-1}
					onClick={() => setOpen(false)}
					className="fixed inset-0 z-30 cursor-default bg-background/50 backdrop-blur-sm animate-in fade-in-0 duration-150"
				/>
			)}
			<div
				ref={containerRef}
				className={cn("relative flex-1", isPanelOpen ? "z-40" : "z-10")}
			>
				<label htmlFor={inputId} className="sr-only">
					Search agents and founders
				</label>
				<div
					className={`group relative overflow-hidden border border-border bg-card transition-colors focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/30 ${joined ? "border-r-0" : ""}`}
				>
					<div className="relative flex min-h-11 items-center gap-3 px-3">
						<span className="font-mono text-sm text-brand select-none">/</span>
						<input
							id={inputId}
							ref={inputRef}
							type="search"
							role="combobox"
							aria-controls={panelId}
							aria-expanded={isPanelOpen}
							aria-autocomplete="list"
							value={query}
							onChange={(event) => {
								setQuery(event.target.value);
								setOpen(true);
							}}
							onFocus={() => setOpen(true)}
							placeholder="search agents and founders"
							autoComplete="off"
							spellCheck={false}
							className="h-11 w-full bg-transparent font-mono text-base text-foreground outline-none placeholder:text-muted-foreground/70"
						/>
						<Search className="size-3.5 shrink-0 text-muted-foreground" />
					</div>
				</div>

				{isPanelOpen && (
					<div
						id={panelId}
						role="listbox"
						aria-label="Search results"
						className="absolute inset-x-0 top-[calc(100%+4px)] z-40 overflow-hidden border border-border bg-card"
					>
						{!isSearchEnabled ? (
							<SearchPanelMessage
								icon={<Search className="size-3.5 text-muted-foreground/60" />}
							>
								Type at least {HOMEPAGE_SEARCH_MIN_QUERY_LENGTH} characters
							</SearchPanelMessage>
						) : isLoading ? (
							<SearchPanelMessage
								icon={
									<Loader2 className="size-3.5 animate-spin text-muted-foreground/60" />
								}
							>
								Searching AgentMRR…
							</SearchPanelMessage>
						) : hasError ? (
							<SearchPanelMessage
								icon={<SearchX className="size-3.5 text-muted-foreground/60" />}
							>
								Search is unavailable. Please try again.
							</SearchPanelMessage>
						) : hasResults ? (
							<div className="max-h-[26rem] overflow-y-auto">
								<SearchResultSection title="Agents" count={agents.length}>
									{agents.length > 0 ? (
										agents.map((agent) => (
											<Link
												key={agent.id}
												to="/agents/$agentSlug"
												params={{
													agentSlug: getHomepageAgentRouteSegment(agent),
												}}
												className="group block w-full"
												onClick={() => setOpen(false)}
											>
												<div className="flex items-center gap-3 px-3 py-2 transition-colors hover:bg-brand-soft">
													<InitialAvatar
														name={agent.displayName ?? agent.name ?? agent.slug}
														category={
															coerceAgentCategories(agent.categories)[0] ?? ""
														}
														logoUrl={agent.logoUrl}
														mode={agent.displayAvatarMode}
														size="agent"
														website={agent.displayWebsite}
													/>
													<div className="min-w-0 flex-1">
														<div className="flex items-center gap-2">
															<p className="truncate text-base font-normal text-foreground transition-colors group-hover:text-brand">
																{agent.displayName ?? agent.name ?? agent.slug}
															</p>
															<span className="hidden shrink-0 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground sm:inline">
																{coerceAgentCategories(agent.categories)
																	.map(getAgentCategoryLabel)
																	.join(" · ")}
															</span>
														</div>
														<p className="mt-0.5 truncate text-sm text-muted-foreground">
															{agent.displayDescription}
														</p>
													</div>
													<ChevronRight className="size-3.5 shrink-0 text-muted-foreground/40 transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-brand" />
												</div>
											</Link>
										))
									) : (
										<SearchSectionEmpty>No matching agents</SearchSectionEmpty>
									)}
								</SearchResultSection>
								<SearchResultSection title="Founders" count={founders.length}>
									{founders.length > 0 ? (
										founders.map((founder) => (
											<Link
												key={founder.slug}
												to="/founders/$founderSlug"
												params={{ founderSlug: founder.slug }}
												className="group block w-full"
												onClick={() => setOpen(false)}
											>
												<div className="flex items-center gap-3 px-3 py-2 transition-colors hover:bg-brand-soft">
													<FounderSearchAvatar founder={founder} />
													<div className="min-w-0 flex-1">
														<p className="truncate text-base font-normal text-foreground transition-colors group-hover:text-brand">
															{founder.name}
														</p>
														<p className="mt-0.5 truncate font-mono text-sm tabular-nums text-muted-foreground">
															{formatFounderSummary(founder, currency)}
														</p>
													</div>
													<ChevronRight className="size-3.5 shrink-0 text-muted-foreground/40 transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-brand" />
												</div>
											</Link>
										))
									) : (
										<SearchSectionEmpty>
											No matching founders
										</SearchSectionEmpty>
									)}
								</SearchResultSection>
							</div>
						) : (
							<SearchPanelMessage
								icon={<SearchX className="size-3.5 text-muted-foreground/60" />}
							>
								No matches for
								<span className="mt-0.5 block font-mono text-foreground">
									"{normalizedDebouncedQuery}"
								</span>
							</SearchPanelMessage>
						)}
					</div>
				)}
			</div>
		</>
	);
}
